/**
 * Tipos de los módulos OpenSIPS del backend (salientes = load_balancer, entrantes = address,
 * dialplan). Alineados con raptor-cpd/src/modules/{salientes,entrantes,dialplan}/domain/*.ts
 * y sus dto/*.schemas.ts.
 *
 * Toda escritura acepta `?dryRun=true`: devuelve `PreviewResult` (la fila como quedaría en la
 * tabla de OpenSIPS, con nombres de columna reales) sin tocar la BD. Sin dryRun escribe y dispara
 * el reload MI correspondiente (lb_reload / address_reload / dp_reload) devolviendo `WriteResult`.
 */

export type PreviewAction = 'create' | 'update' | 'delete' | 'capacity';

/** Fila "como la tabla": llaves = columnas reales de OpenSIPS. `id` null en preview de alta. */
export type ColumnView = Record<string, string | number | null>;

export interface PreviewResult {
  action: PreviewAction;
  record: ColumnView;
}

export interface OpensipsReloadResult {
  triggered: boolean;
  ok: boolean;
  message: string;
}

export interface WriteResult<T = any> {
  record: T;
  reload: OpensipsReloadResult;
}

export interface DeleteResult {
  deleted: true;
  id: number;
  reload: OpensipsReloadResult;
}

// ----- Salientes (load_balancer) -------------------------------------------
export interface LbResource {
  resource: string;
  capacity: number;
}

export interface SalienteDto {
  id: number;
  groupId: number;
  dstUri: string;
  resources: LbResource[];
  probeMode: number;
  description: string | null;
  maxChannels: number | null;
  /** derivado: alguna capacidad > 0 */
  active: boolean;
}

export interface SalienteUpsert {
  groupId: number;
  dstUri: string;
  resources: LbResource[];
  /** 0 = sin probing, 1 = solo inactivos, 2 = siempre */
  probeMode: number;
  description?: string | null;
  maxChannels?: number | null;
}

export interface SetCapacityInput {
  resource: string;
  capacity: number;
}

// ----- Entrantes (address) ------------------------------------------------
export const PROTO_VALUES = ['any', 'udp', 'tcp', 'tls', 'sctp'] as const;
export type Proto = (typeof PROTO_VALUES)[number];

export interface EntranteDto {
  id: number;
  grp: number;
  ip: string;
  mask: number;
  port: number;
  proto: string;
  pattern: string | null;
  contextInfo: string | null;
  description: string | null;
}

export interface EntranteUpsert {
  grp: number;
  ip: string;
  mask: number;
  port: number;
  proto: Proto;
  pattern?: string | null;
  contextInfo?: string | null;
  description?: string | null;
}

// ----- Dialplan -----------------------------------------------------------
export const MATCH_OP_VALUES = ['equal', 'regex'] as const;
export type MatchOp = (typeof MATCH_OP_VALUES)[number];

export interface DialplanDto {
  id: number;
  dpid: number;
  pr: number;
  matchOp: MatchOp;
  matchExp: string | null;
  matchFlags: number;
  substExp: string | null;
  replExp: string | null;
  timerec: string | null;
  enabled: boolean;
  attrs: string | null;
  description: string | null;
}

export interface DialplanUpsert {
  dpid: number;
  pr: number;
  matchOp: MatchOp;
  matchExp: string;
  matchFlags: number;
  substExp?: string | null;
  replExp?: string | null;
  timerec?: string | null;
  enabled: boolean;
  attrs?: string | null;
  description?: string | null;
}
